import {BuildingOfficeIcon, GlobeAmericasIcon, UsersIcon} from "@heroicons/react/16/solid";
import {ReactNode} from "react";
import {OtsBadge, OtsBadgeTone} from "../components/OtsBadge";
import {OtsActionButton} from "../components/OtsButton";
import {OtsInlineAlert} from "../components/OtsInlineAlert";

export type NoticeListItem = {
  id: string;
  title: string;
  body?: string;
  /** INFO, MAJOR, or CRITICAL. */
  severity: string;
  category?: string;
  createdAt?: string;
  createdBy?: string;
  /** "Expires …", "Expired …", or nothing for a notice with no expiry. */
  expiry?: string;
  expired?: boolean;
  /** Who the notice was sent to. */
  scopes?: {kind: "region" | "company" | "audience"; label: string}[];
};

export type NoticeListProps = {
  notices: NoticeListItem[];
  isLoading?: boolean;
  error?: ReactNode;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onRetry?: () => void;
};

const severityTones: Record<string, OtsBadgeTone> = {CRITICAL: "danger", MAJOR: "warning", INFO: "info"};
const scopeIcons = {region: GlobeAmericasIcon, company: BuildingOfficeIcon, audience: UsersIcon};
const scopeTones: Record<string, OtsBadgeTone> = {region: "blue", company: "yellow", audience: "violet"};

/** Active notices, newest first as the server sends them: what it says, how serious, who sent it and to whom. */
export const NoticeList = ({notices, isLoading = false, error, hasMore = false, onLoadMore, onRetry}: NoticeListProps) => {
  if (error != null) {
    return <OtsInlineAlert
      tone="danger"
      size="compact"
      title="Notices could not be loaded"
      action={onRetry && <OtsActionButton size="compact" onClick={onRetry}>Try again</OtsActionButton>}>
      {error}
    </OtsInlineAlert>;
  }
  if (isLoading && !notices.length) return <p role="status" className="text-sm text-[var(--ots-text-muted)]">Loading notices…</p>;
  if (!notices.length) return <p className="text-sm text-[var(--ots-text-muted)]">No active notices.</p>;

  return <>
    <ul aria-label="Notices" className="space-y-2">
      {notices.map(notice => (
        <li
          key={notice.id}
          className={[
            "rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] p-3 shadow-xs",
            notice.severity === "CRITICAL" ? "shadow-[inset_3px_0_0_var(--ots-error)]" : notice.severity === "MAJOR" ? "shadow-[inset_3px_0_0_var(--ots-warning)]" : "",
            notice.expired ? "opacity-60" : "",
          ].join(" ")}>
          <div className="flex items-start gap-2">
            <h3 className="min-w-0 flex-1 text-sm font-semibold">{notice.title}</h3>
            <OtsBadge tone={severityTones[notice.severity] ?? "neutral"} size="compact">{notice.severity.toLowerCase()}</OtsBadge>
          </div>
          <p className="mt-0.5 text-xs text-[var(--ots-text-muted)]">
            {[notice.category, notice.createdAt, notice.createdBy].filter(Boolean).join(" · ")}
          </p>
          {notice.body && <p className="mt-2 whitespace-pre-line text-sm leading-5">{notice.body}</p>}
          {!!notice.scopes?.length && (
            <ul aria-label="Sent to" className="mt-2 flex flex-wrap gap-1">
              {notice.scopes.map(scope => {
                const Icon = scopeIcons[scope.kind];
                return <li key={`${scope.kind}-${scope.label}`}><OtsBadge tone={scopeTones[scope.kind]} size="compact" icon={<Icon/>}>{scope.label}</OtsBadge></li>;
              })}
            </ul>
          )}
          {notice.expiry && <p className="mt-2 text-[11px] text-[var(--ots-text-muted)]">{notice.expiry}</p>}
        </li>
      ))}
    </ul>
    {onLoadMore && (
      <OtsActionButton variant="tonal" size="compact" className="mt-3 w-full" disabled={!hasMore || isLoading} onClick={onLoadMore}>
        {isLoading ? "Loading…" : hasMore ? "Load more" : "No more notices"}
      </OtsActionButton>
    )}
  </>;
};
